import React, { useEffect, useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { TrendUp, ArrowDown, Download } from "@phosphor-icons/react";

const STAGES = [
  { k: "requests_started", label: "Requests started" },
  { k: "requests_submitted", label: "Submitted" },
  { k: "quotes_issued", label: "Quotes issued" },
  { k: "quotes_approved", label: "Approved" },
  { k: "payments_succeeded", label: "Paid" },
  { k: "orders_delivered", label: "Delivered" },
];

function SampleSparkline({ series }) {
  const max = Math.max(...series.map((s) => s.views), 1);
  const w = 800, h = 80, pad = 4;
  const step = (w - pad * 2) / (series.length - 1 || 1);
  const points = series.map((s, i) => {
    const x = pad + i * step;
    const y = h - pad - ((s.views / max) * (h - pad * 2));
    return `${x},${y}`;
  });
  const path = "M " + points.join(" L ");
  const area = `${path} L ${pad + (series.length - 1) * step},${h - pad} L ${pad},${h - pad} Z`;
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" data-testid="sample-sparkline">
        <path d={area} fill="hsl(var(--primary))" opacity="0.08" />
        <path d={path} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
        {series.map((s, i) => (
          s.views > 0 ? <circle key={s.date} cx={pad + i * step} cy={h - pad - ((s.views / max) * (h - pad * 2))} r="2" fill="hsl(var(--primary))" /> : null
        ))}
      </svg>
      <div className="flex justify-between text-[10px] font-mono text-muted-foreground mt-1">
        <span>{series[0]?.date}</span>
        <span>Peak: {max} view{max === 1 ? "" : "s"}/day</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [funnel, setFunnel] = useState(null);
  const [acq, setAcq] = useState(null);
  const [sample, setSample] = useState(null);

  useEffect(() => {
    api.get("/analytics/funnel").then((r) => setFunnel(r.data));
    api.get("/analytics/acquisition").then((r) => setAcq(r.data)).catch((e) => console.warn("[AdminAnalytics] acquisition failed:", e));
    api.get("/analytics/sample-views").then((r) => setSample(r.data)).catch((e) => console.warn("[AdminAnalytics] sample-views failed:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportUrl = (name) => `${process.env.REACT_APP_BACKEND_URL}/api/analytics/export/${name}.csv`;

  if (!funnel) return <div className="p-10">Loading…</div>;
  const maxCount = Math.max(...STAGES.map((s) => funnel.counts[s.k] || 0), 1);

  return (
    <div className="App min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">Analytics</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl md:text-5xl mb-8">Commercial funnel.</h1>

        {/* Funnel bars */}
        <div className="border border-border bg-white p-6 mb-8">
          <div className="space-y-4">
            {STAGES.map((s, i) => {
              const v = funnel.counts[s.k] || 0;
              const pct = (v / maxCount) * 100;
              const prev = i > 0 ? (funnel.counts[STAGES[i - 1].k] || 0) : v;
              const dropPct = prev ? Math.round(((prev - v) / prev) * 100) : 0;
              return (
                <div key={s.k} data-testid={`funnel-${s.k}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium">{s.label}</div>
                    <div className="text-sm font-mono">
                      <b className="text-foreground">{v}</b>
                      {i > 0 && dropPct > 0 && (
                        <span className="text-muted-foreground ml-2 text-xs">
                          <ArrowDown size={10} className="inline mr-0.5" /> {dropPct}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-8 bg-muted relative">
                    <div
                      className={`h-full ${i === STAGES.length - 1 ? "bg-emerald-500" : "bg-primary"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rates */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { k: "submit_rate", label: "Start → Submit" },
            { k: "approve_rate", label: "Quote → Approve" },
            { k: "pay_rate", label: "Approve → Paid" },
            { k: "deliver_rate", label: "Paid → Delivered" },
            { k: "repeat_rate", label: "Repeat customers" },
            { k: "replacement_rate", label: "Replacement rate" },
          ].map((r) => (
            <div key={r.k} className="border border-border bg-white p-4">
              <div className="text-[11px] tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">{r.label}</div>
              <div className="font-heading font-black text-3xl">{funnel.rates[r.k]}%</div>
            </div>
          ))}
        </div>

        {/* Exports */}
        <div className="border border-border bg-white p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-semibold">CSV exports</div>
              <div className="text-xs text-muted-foreground">Filtered by role. Files download over authenticated session.</div>
            </div>
            <TrendUp size={20} className="text-muted-foreground" />
          </div>
          <div className="flex flex-wrap gap-2">
            {["orders", "payments", "requests", "audit"].map((n) => (
              <a key={n} href={exportUrl(n)} download data-testid={`export-${n}`}>
                <Button variant="outline" className="rounded-sm">
                  <Download size={14} className="mr-2" /> {n}.csv
                </Button>
              </a>
            ))}
          </div>
        </div>

        {/* Acquisition */}
        {acq && (
          <div className="border border-border bg-white p-6 mb-8">
            <div className="font-semibold mb-4">Acquisition sources · {acq.total} sessions</div>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              {[["by_source", "Source"], ["by_medium", "Medium"], ["by_campaign", "Campaign"]].map(([k, l]) => (
                <div key={k}>
                  <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">{l}</div>
                  {Object.entries(acq[k]).length === 0 && <div className="text-muted-foreground text-xs">—</div>}
                  {Object.entries(acq[k]).map(([name, count]) => (
                    <div key={name} className="flex justify-between border-b border-border py-1.5 text-xs">
                      <span className="truncate">{name || "(none)"}</span>
                      <span className="font-mono">{count}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sample View Analytics */}
        {sample && (
          <div className="border border-border bg-white p-6" data-testid="sample-analytics">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-semibold">Sample link engagement</div>
                <div className="text-xs text-muted-foreground">Track which prospects actually opened the shared sample.</div>
              </div>
              <div className="text-right">
                <div className="font-heading font-black text-3xl">{sample.total_views}</div>
                <div className="text-[10px] uppercase tracking-widest-2 text-muted-foreground font-semibold">total views</div>
              </div>
            </div>
            {/* Sparkline (SVG) — last 30 days */}
            <SampleSparkline series={sample.series_30d} />

            <div className="mt-6">
              <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Top requests by sample views</div>
              {sample.top_requests.length === 0 && <div className="text-sm text-muted-foreground">No sample shares yet.</div>}
              <div className="divide-y divide-border">
                {sample.top_requests.map((r) => (
                  <div key={r.request_id} className="py-3 flex items-center justify-between text-sm" data-testid={`sample-top-${r.request_id}`}>
                    <div className="min-w-0">
                      <div className="font-mono text-[11px] text-muted-foreground">{r.reference}</div>
                      <div className="font-medium truncate">{r.title}</div>
                      {r.last_viewed && <div className="text-[11px] text-muted-foreground">Last viewed {new Date(r.last_viewed).toLocaleString()}</div>}
                    </div>
                    <div className="text-right ml-4">
                      <div className="font-heading font-bold text-2xl">{r.views}</div>
                      <div className="text-[10px] uppercase tracking-widest-2 text-muted-foreground">views</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
